import React, { useEffect, useState, useMemo } from 'react';
import '@d3fc/d3fc-element';
import { extentLinear } from '@d3fc/d3fc-extent';
import TimeSeriesChart from 'utils/components/TimeSeriesChart/TimeSeriesChart';
import { combineToggleableClassnames } from 'utils/toggleableClassnames/toggleableClassnames';
import {
    useYScale,
    percentageFormat,
} from 'utils/components/TimeSeriesChart/yAxis';
import {
    FillsGraphData,
    deriveBucketPercentOfMarketVolume,
} from 'widgets/fillsGraph/fillsGraphData';
import YAxisLabel from '../axisLabel/YAxisLabel';
import PercentageOfMarketVolumeChartLegend from './PercentageOfMarketVolumeChartLegend';
import {
    FillsChartType,
    PercentageOfMarketVolumeConfig,
} from '../store/configuration/FillsGraphConfiguration';
import { percentageOfMarketVolumeSeries } from './percentageOfMarketVolumeChartSeries';
import { getChartData } from './percentageOfMarketVolumeChartData';
import { derivePercentageOfMarketYAxisLabelProps } from './percentageOfMarketVolumeChartLabels';
import './PercentageOfMarketVolumeChart.scss';
import styleConstants from '../variables.module.scss';
import { useShowCollapsableCompanionChart } from 'widgets/fillsGraph/collapsableCompanionCharts/useShowCollapsableCompanionChart';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { useHorizontalZoomContext } from 'utils/components/Zoom/ZoomContext';

const xAxisHeight = parseInt(styleConstants.xAxisHeight);

/* eslint-disable react-hooks/exhaustive-deps */

export interface PercentageOfMarketVolumeData {
    midTime: number;
    percentOfMarketVolume: number;
    averagePercentOfMarketVolume: number;
    bucketWidth: number;
}

export interface PercentageOfMarketVolumeChartProps {
    width: number;
    height: number;
    chartHeight: number;
    fillsGraphContainerLeft: number;
    fillsGraphData: FillsGraphData;
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    config: PercentageOfMarketVolumeConfig;
    displayXAxis: boolean;
}

export default function PercentageOfMarketVolumeChart(
    props: PercentageOfMarketVolumeChartProps
) {
    const {
        width,
        height,
        chartHeight,
        fillsGraphData,
        fillsGraphPortfolioData,
        config,
        displayXAxis,
        fillsGraphContainerLeft,
    } = props;
    const {
        data,
        bucketedData,
        sideClass,
        timeCompleted,
        strategyParameterData,
    } = fillsGraphData;
    const {
        displayPercentageOfVolume,
        displayAvgPercentageOfVolume,
        displayMinParticipationRate,
        displayMaxParticipationRate,
    } = config;

    const renderCompanionChart = useShowCollapsableCompanionChart(
        FillsChartType.PercentageOfMarketVolume
    );

    const { intervalToWidth } = useHorizontalZoomContext();

    const chartData = useMemo(
        () => getChartData(bucketedData, timeCompleted, intervalToWidth),
        [bucketedData, timeCompleted, intervalToWidth]
    );

    const yDomain =
        bucketedData.length > 0 &&
        extentLinear()
            .accessors([deriveBucketPercentOfMarketVolume, () => 0])
            .pad([0, 0.1])(chartData);
    const yScale = useYScale(yDomain, [0, 1]);
    const yTicks = 3;

    const [series, setSeries] = useState();
    useEffect(() => {
        setSeries(() => percentageOfMarketVolumeSeries(yTicks, chartData));
    }, [yDomain[0], yDomain[1], yTicks, chartData]);

    const cssClasses = combineToggleableClassnames(
        { classname: 'PercentageOfMarketVolumeChart' },
        { classname: 'companion-chart' },
        { classname: 'hide-x-axis', hideClassname: displayXAxis },

        { classname: sideClass },
        {
            classname: 'hide-avg-percent-line',
            hideClassname: displayAvgPercentageOfVolume,
        },
        {
            classname: 'hide-percent-bars',
            hideClassname: displayPercentageOfVolume,
        },
        {
            classname: 'hide-min-participation-line',
            hideClassname: displayMinParticipationRate,
        },
        {
            classname: 'hide-max-participation-line',
            hideClassname: displayMaxParticipationRate,
        }
    );

    const stratParams =
        strategyParameterData?.[strategyParameterData.length - 1];
    const minParticipationRate = stratParams?.minParticipationRate;
    const maxParticipationRate = stratParams?.maxParticipationRate;

    const labelProps = derivePercentageOfMarketYAxisLabelProps(
        displayMinParticipationRate ? minParticipationRate : undefined,
        displayMaxParticipationRate ? maxParticipationRate : undefined,
        yScale,
        chartHeight - xAxisHeight,
        width
    );
    const style = useMemo(() => ({ height }), [height]);

    return (
        <div className={cssClasses} style={style}>
            <PercentageOfMarketVolumeChartLegend
                fillsGraphData={fillsGraphData}
                fillsGraphPortfolioData={fillsGraphPortfolioData}
                fillsGraphContainerLeft={fillsGraphContainerLeft}
            />
            {renderCompanionChart && (
                <div className="chart-container">
                    {labelProps.minParticipationRate && (
                        <YAxisLabel {...labelProps.minParticipationRate} />
                    )}
                    {labelProps.maxParticipationRate && (
                        <YAxisLabel {...labelProps.maxParticipationRate} />
                    )}
                    <TimeSeriesChart
                        id="PercentageOfMarketVolumeChart"
                        height={chartHeight}
                        data={data}
                        series={series}
                        displayXAxis={displayXAxis}
                        yScale={yScale}
                        yTicks={yTicks}
                        yTickFormat={percentageFormat}
                    />
                </div>
            )}
        </div>
    );
}
